import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import TsdPageBar, { TsdCabinetSelect } from '../components/tsd/TsdPageBar';
import FulfillmentStatusChip from '../components/fulfillment/FulfillmentStatusChip';
import useFulfillmentCabinet from '../hooks/useFulfillmentCabinet';
import {
  fulfillmentApiBase,
  fulfillmentTsdPath,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';
import {
  isActivePickingStatus,
  pickingProgress,
} from './pickingUi.mjs';

function errorText(error) {
  return error?.response?.data?.message || error.message || 'Не удалось загрузить задания';
}

export default function FulfillmentPicking() {
  const navigate = useNavigate();
  const {
    accounts,
    accountsLoading,
    accountsError,
    accountId,
    setAccountId,
    marketplace,
    isLocalWarehouse,
  } = useFulfillmentCabinet();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const apiBase = fulfillmentApiBase(marketplace);

  const load = useCallback(async () => {
    if (isLocalWarehouse && !accountId) {
      setJobs([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/picking-jobs`, {
        params: withAccountParams(marketplace, accountId, isLocalWarehouse ? { page: 1, page_size: 100 } : {}),
      });
      const items = (data?.items || []).filter((job) => isActivePickingStatus(job.status));
      setJobs(items);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBase, isLocalWarehouse, marketplace]);

  useEffect(() => { load(); }, [load]);

  const openJob = (job) => {
    navigate(fulfillmentTsdPath(`/fulfillment/picking/${job.id}`, {
      mp: marketplace,
      accountId: job.account_id || accountId,
    }));
  };

  return (
    <>
      <TsdPageBar
        title="Подбор"
        extra={(
          <>
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
              <TsdCabinetSelect
                id="picking-account"
                accounts={accounts}
                accountId={accountId}
                disabled={accountsLoading}
                onChange={setAccountId}
              />
            </Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={loading || accountsLoading || (isLocalWarehouse && !accountId)}
              sx={{ minHeight: 40 }}
            >
              Обновить
            </Button>
          </>
        )}
      />
      <Stack
        spacing={2}
        sx={{
          maxWidth: 900,
          mx: 'auto',
          pb: 4,
          width: '100%',
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        {(error || accountsError) && (
          <Alert severity="error" onClose={() => setError('')}>
            {error || accountsError}
          </Alert>
        )}

        {!accountsLoading && !accounts.length && (
          <Alert severity="warning">Нет доступных кабинетов</Alert>
        )}

        {isLocalWarehouse && !accountId && !accountsLoading && accounts.length > 0 && (
          <Alert severity="warning">Выберите кабинет</Alert>
        )}

        {(loading || accountsLoading) && <LinearProgress />}
        {!loading && !jobs.length && !(isLocalWarehouse && !accountId) && (
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">Активных заданий нет</Typography>
          </Paper>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5, minWidth: 0 }}>
          {jobs.map((job) => {
            const jobProgress = pickingProgress(job);
            return (
              <Card key={job.id} variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
                <CardContent sx={{ minWidth: 0 }}>
                  <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      gap={1}
                      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                      sx={{ minWidth: 0 }}
                    >
                      <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                        <Typography
                          variant="titleMedium"
                          sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                        >
                          {job.code || job.name}
                        </Typography>
                        {job.code && (
                          <Typography
                            variant="bodySmall"
                            color="text.secondary"
                            sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            {job.name}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <FulfillmentStatusChip kind="pickingJob" status={job.status} />
                      </Box>
                    </Stack>
                    <LinearProgress variant="determinate" value={jobProgress.percent} sx={{ height: 8, borderRadius: 4 }} />
                    <Typography variant="bodyMedium">
                      {jobProgress.picked} из {jobProgress.total} подобрано
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<OpenIcon />}
                      onClick={() => openJob(job)}
                      sx={{ minHeight: 52, alignSelf: 'stretch' }}
                    >
                      Открыть
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Stack>
    </>
  );
}
